import { longStringFromDate } from '@daymade/common-lib/formatting';
import GenericModal from 'components/GenericModal';
import RoundedButton from 'components/RoundedButton';
import SnoozeDurationSelectionSection from 'components/subscriptions/SnoozeDurationSelectionSection';
import { SNOOZE_DURATION_OPTIONS } from 'components/subscriptions/SnoozeSubscriptionSection';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { trackEvent } from 'utils/analytics/analytics';
import { subscriptionApiClient } from 'utils/apiClient';
import { getFormattedSnoozeDuration } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

const SnoozeSubscriptionPopup = observer(
  ({
    subscriptionId,
    onClose,
    onSuccess,
  }: {
    subscriptionId: string;
    onClose: () => void;
    onSuccess: () => void;
  }) => {
    const { displayToast } = useUIStore();
    const { updateCurrentUser } = useUserStore();

    const [loading, setLoading] = useState(false);

    const [snoozeDurationSelected, setSnoozeDurationSelected] =
      useState<number>(1);
    const validSnoozeDurations = SNOOZE_DURATION_OPTIONS;

    const handleSnoozeSubscriptionPressed = async () => {
      setLoading(true);
      const result = await subscriptionApiClient.auth.snoozeSubscription({
        params: {
          subscription_id: subscriptionId,
        },
        body: {
          snoozeDuration: snoozeDurationSelected,
          snoozeUnits: validSnoozeDurations.unit,
        },
      });
      setLoading(false);

      if (result.status === HttpStatusCode.OK) {
        const { snoozedUntil } = result.body;
        displayToast({
          title: `Your subscription has been snoozed for ${getFormattedSnoozeDuration(
            snoozeDurationSelected,
            validSnoozeDurations.unit
          )}. It will renew on or around ${longStringFromDate(snoozedUntil)}`,
          timeout: 9500,
        });
        onSuccess();

        await updateCurrentUser();

        trackEvent('SubscriptionSnoozed', {
          source: 'header',
          snoozeDuration: snoozeDurationSelected,
          unit: validSnoozeDurations.unit,
        });
      } else {
        displayToast({
          title: `Something went wrong while trying to snooze your subscription. Please drop us an email for details: hello@daymade.co.uk`,
          color: '#FF6600',
          timeout: 9500,
        });
        onClose();
      }
    };

    return (
      <GenericModal onClose={onClose}>
        <img
          src={`${ASSET_PREFIX}/images/snooze-switch-section/snooze-icon.svg`}
          alt=""
          style={{ height: 70 }}
        />

        <p style={{ fontWeight: 800, fontSize: '1.3rem', marginTop: '1em' }}>
          Snooze subscription
        </p>

        <SnoozeDurationSelectionSection
          durations={validSnoozeDurations?.durations}
          durationUnit={validSnoozeDurations?.unit}
          selectedDuration={snoozeDurationSelected}
          onSelectHandler={setSnoozeDurationSelected}
        />

        <RoundedButton
          loading={loading}
          onClick={handleSnoozeSubscriptionPressed}
          style={{ margin: '2em 0 1em', width: '100%', maxWidth: 260 }}
        >
          Snooze subscription
        </RoundedButton>

        <p
          onClick={onClose}
          style={{ cursor: 'pointer', opacity: 0.5, fontWeight: 700 }}
        >
          Close
        </p>
      </GenericModal>
    );
  }
);

export default SnoozeSubscriptionPopup;
